/**
 * Video-comment Mixpanel events + typed tracker.
 * Fired across the comments organism (`<vm-video-comments>`), composer
 * (`<vm-video-comment-composer>`), and list-item
 * (`<vm-video-comments-list-item>`) as the user posts, uploads, and opens
 * the mobile sheet.
 *
 * The video context (`video_id` / `video_slug` / `channel_id` /
 * `template_version`) is auto-enriched from `window.__VUMEDI_VIDEO_DETAIL_CONTEXT__`,
 * so callers only pass the event-specific props. Calls are dropped cleanly
 * when the video-detail bundle hasn't set the context yet.
 *
 * Use `trackVideoCommentEvent` rather than calling `window.vumedi?.mixpanel?.track`
 * directly: the helper enforces that the payload matches the event via the
 * `VideoCommentEventPayloads` map.
 *
 * The pre-existing `VideoCommentThumbupToggled` event is intentionally NOT
 * routed through this helper — it carries additional per-comment props
 * (`comment_id`, `comment_author_id`, `thumbup_enabled`) and the tech spec
 * calls out that it continues unchanged. See `vm-video-comments-list-item.ts`.
 */

export const VideoCommentEvents = {
    POSTED: 'VideoCommentPosted',
    REPLY_POSTED: 'VideoCommentReplyPosted',
    ATTACHMENT_UPLOAD_ATTEMPTED: 'VideoCommentAttachmentUploadAttempted',
    ATTACHMENT_UPLOAD_REJECTED: 'VideoCommentAttachmentUploadRejected',
    ATTACHMENT_UPLOAD_FAILED: 'VideoCommentAttachmentUploadFailed',
    SHEET_OPENED: 'VideoCommentsSheetOpened',
    SHEET_CLOSED: 'VideoCommentsSheetClosed',
} as const;

export type VideoCommentEventName = (typeof VideoCommentEvents)[keyof typeof VideoCommentEvents];

/** Shared post-body shape sent with both a top-level POSTED and a REPLY_POSTED
 * event. `comment_id` / `is_public` / `attachment_count` were on the pre-existing
 * inline `VideoCommentPosted` call and are retained so Mixpanel dashboards that
 * join on `comment_id` or filter on `is_public` don't lose data. */
export interface VideoCommentPostedCoreProps {
    comment_id: number | null;
    is_public: boolean | null;
    attachment_count: number;
}

/** Uniform per-file shape carried by attachment events. `extension` is the
 * trailing filename extension (no dot, lowercased); `file_size` is in bytes. */
export interface AttachmentFileInfo {
    extension: string;
    file_size: number;
}

/** Attachment file plus the client-side validation branch that rejected it.
 * `'type'` fires per file that fails the MIME whitelist (JPG/PNG/GIF/PDF).
 * `'per_file_size'` fires per file over the 25 MB per-file cap.
 * `'count'` fires against every file in a pick that would push the comment
 * over the 10-file cap — no single offender when the total tips over. */
export interface RejectedAttachmentFileInfo extends AttachmentFileInfo {
    reason: 'type' | 'per_file_size' | 'count';
}

export interface VideoCommentEventPayloads {
    [VideoCommentEvents.POSTED]: VideoCommentPostedCoreProps;
    /** `top_level_comment_id` is optional — the composer emits `replyToId`
     * (the immediate parent's id) but not the top-level ancestor. Populating
     * it requires walking the tanstack cache tree at submit time; skip until
     * a dashboard actually needs it. */
    [VideoCommentEvents.REPLY_POSTED]: VideoCommentPostedCoreProps & {
        reply_to_comment_id: number;
        top_level_comment_id?: number;
    };
    [VideoCommentEvents.ATTACHMENT_UPLOAD_ATTEMPTED]: { files: AttachmentFileInfo[] };
    /** `files` contains only the newly-picked files that got dropped in this
     * pick action — already-attached files stay attached and aren't repeated
     * in the payload. */
    [VideoCommentEvents.ATTACHMENT_UPLOAD_REJECTED]: {
        files: RejectedAttachmentFileInfo[];
    };
    /** Fires per-file when the network upload fails after client-side
     * validation passed. Chip is dropped and a rejection modal opens for
     * each failure. */
    [VideoCommentEvents.ATTACHMENT_UPLOAD_FAILED]: {
        files: AttachmentFileInfo[];
    };
    [VideoCommentEvents.SHEET_OPENED]: Record<string, never>;
    [VideoCommentEvents.SHEET_CLOSED]: Record<string, never>;
}

/** Returns the trailing filename extension without the dot, lowercased.
 * `photo.PNG` → `png`, `readme` → `""`, `.DS_Store` → `""` (dotfiles have no
 * extension — a leading dot marks the whole name as hidden). Used by
 * attachment-event payloads so the composer + organism agree on how they
 * normalise extensions. */
export function extractFileExtension(filename: string): string {
    const dot = filename.lastIndexOf('.');
    if (dot <= 0) return '';
    return filename.slice(dot + 1).toLowerCase();
}

export function trackVideoCommentEvent<E extends VideoCommentEventName>(
    event: E,
    props: VideoCommentEventPayloads[E],
): void {
    const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
    if (!context?.videoId) return;
    // Swallow any throw from mixpanel-browser so the surrounding comment flow
    // (submit / upload / sheet-transition) can't be broken by an analytics
    // failure. Consumers call this from load-bearing paths — an unguarded
    // throw would strand `_loading = true` or skip an optimistic cache write.
    try {
        window.vumedi?.mixpanel?.track(event, {
            video_id: context.videoId,
            video_slug: context.videoSlug,
            channel_id: context.channelId,
            template_version: context.templateVersion,
            ...props,
        });
    } catch {
        // Intentionally silent — Mixpanel's own error handling reports internally.
    }
}
